/**
 * Returns a function that, as long as it continues to be invoked, will not
 * be triggered. The function will be called after it stops being called for
 * N milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing. The returned function has `.clear`
 * and `.flush` to cancel or run a pending call.
 *
 * Aligned with the previous `debounce@1.2.1` package API (underscore-style).
 */
export function debounce<T extends (...args: never[]) => unknown> (
  func: T,
  wait?: number | null,
  immediate?: boolean
): T & { clear: () => void, flush: () => void } {
  let timeout: ReturnType<typeof setTimeout> | null

  let savedArgs: Parameters<T> | null
  let context: unknown
  let timestamp = 0

  let result: ReturnType<T> | undefined

  const waitMs = wait == null ? 100 : wait

  function later (): void {
    const last = Date.now() - timestamp

    if (last < waitMs && last >= 0) {
      timeout = setTimeout(later, waitMs - last)
    } else {
      timeout = null
      if (!immediate) {
        result = func.apply(context, savedArgs as Parameters<T>)
        context = savedArgs = null
      }
    }
  }

  const debounced = function (this: unknown, ...args: Parameters<T>) {
    context = this
    savedArgs = args
    timestamp = Date.now()
    const callNow = Boolean(immediate && !timeout)
    if (!timeout) timeout = setTimeout(later, waitMs)
    if (callNow) {
      result = func.apply(context, savedArgs)
      context = savedArgs = null
    }

    return result
  } as T & { clear: () => void, flush: () => void }

  debounced.clear = function (): void {
    if (timeout) {
      clearTimeout(timeout)
      timeout = null
    }
  }

  debounced.flush = function (): void {
    if (timeout) {
      result = func.apply(context, savedArgs as Parameters<T>)
      context = savedArgs = null

      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
    }
  }

  return debounced
}

// Same as the legacy `debounce@1.2.1` export shape
; (debounce as unknown as { debounce: typeof debounce }).debounce = debounce
